import { tuneProjectionAllTrue, talknClients } from '@common/talkn-config';
import type { TuneProjection } from '@common/talkn-config';
import type { TalknWidgetsConfig } from './src/TalknWidgetsConfig';
import { TalknWidgetsConfigManager } from './src/TalknWidgetsConfig';
// releaseSharedConnection is intentionally NOT imported from '@api-client/TalknAPI' here.
// Each widget bundle has its own module scope (and its own sharedConnections Map), so a
// local import would reference a different Map than the one populated by window.TalknAPI.
// We use window.TalknReleaseSharedConnection, which is set by the first-loaded api-client
// bundle and therefore shares the same closure as window.TalknAPI.
const releaseSharedConnection = (connection: string, connectionMode: 'tune-shared' | 'observe'): Promise<void> => {
  return window.TalknReleaseSharedConnection?.(connection, connectionMode) ?? Promise.resolve();
};

export { talknClients };
export type { TuneProjection, TuneProjectionKey } from '@common/talkn-config';
export type { TalknWidgetsConfig } from './src/TalknWidgetsConfig';

// Build-time injected config from DB (via webpack DefinePlugin) — deprecated
declare const __TALKN_APPS_CONFIG__: Record<string, { className: string; targetCh: string; excludeUrlPattern: string }> | undefined;

const injectedConfig = typeof __TALKN_APPS_CONFIG__ !== 'undefined' ? __TALKN_APPS_CONFIG__ : undefined;

// Runtime config fetched from public API
type RuntimeAppConfig = {
  className: string;
  targetCh: string;
  excludeUrlPattern: string;
  conversionChRegex: string;
  themePreset?: string;
  uiShowIcon?: boolean;
  uiPostPosition?: string;
  uiPostStickyBottom?: number;
  uiPostBorderTop?: boolean;
  uiPostBorderRight?: boolean;
  uiPostBorderBottom?: boolean;
  uiPostBorderLeft?: boolean;
  uiIncludeChildren?: boolean;
  trackingApiKey?: string;
};
export type { RuntimeAppConfig };
type RuntimeConfig = Record<string, RuntimeAppConfig>;

let runtimeConfig: RuntimeConfig | null | undefined = undefined;
let runtimeApiKey: string | undefined = undefined;

type TalknApiFunction = typeof window.TalknAPI & {
  __talknBaseAPI?: typeof window.TalknAPI;
};

const applyTalknApiDefaults = (): void => {
  if (typeof window === 'undefined' || typeof window.TalknAPI !== 'function') return;

  const config = TalknWidgetsConfigManager.getConfig();
  const defaultApiKey = config.apiKey || runtimeApiKey;
  const defaultHost = config.host;
  const defaultProtocol = config.protocol;

  if (!defaultApiKey && !defaultHost && !defaultProtocol) return;

  const current = window.TalknAPI as TalknApiFunction;
  const base = current.__talknBaseAPI || current;
  const wrapped = ((connection: string, params: Record<string, unknown> = {}) => {
    return base(connection, {
      ...(defaultHost ? { host: defaultHost } : {}),
      ...(defaultProtocol ? { protocol: defaultProtocol } : {}),
      ...(defaultApiKey ? { apiKey: defaultApiKey } : {}),
      ...params,
      apiKey: (params as { apiKey?: string }).apiKey || defaultApiKey,
    });
  }) as TalknApiFunction;

  wrapped.__talknBaseAPI = base;
  window.TalknAPI = wrapped;
};

async function fetchRuntimeConfig(): Promise<RuntimeConfig | undefined> {
  if (typeof window === 'undefined') return undefined;

  const { hostname } = window.location;
  try {
    const base = hostname === 'widgets.talkn.io'
      ? ''
      : 'https://admin.talkn.io';
    const params = new URLSearchParams({ domain: hostname });

    const res = await fetch(`${base}/api/public/apps-config?${params}`, { signal: AbortSignal.timeout(1000) });

    if (!res.ok) return undefined;
    const configs = await res.json();
    if (!Array.isArray(configs) || configs.length === 0) return undefined;

    const result: RuntimeConfig = {};
    for (const cfg of configs) {
      if (!runtimeApiKey && typeof cfg.trackingApiKey === 'string') {
        runtimeApiKey = cfg.trackingApiKey;
      }
      const key = cfg.className || 'talkn-card';
      result[key] = {
        className: cfg.className || '',
        targetCh: cfg.targetCh || '/',
        excludeUrlPattern: cfg.excludeUrlPattern || '',
        conversionChRegex: cfg.conversionChRegex || '',
        themePreset: cfg.themePreset,
        uiShowIcon: cfg.uiShowIcon,
        uiPostPosition: cfg.uiPostPosition,
        uiPostStickyBottom: cfg.uiPostStickyBottom,
        uiPostBorderTop: cfg.uiPostBorderTop,
        uiPostBorderRight: cfg.uiPostBorderRight,
        uiPostBorderBottom: cfg.uiPostBorderBottom,
        uiPostBorderLeft: cfg.uiPostBorderLeft,
        uiIncludeChildren: cfg.uiIncludeChildren,
        trackingApiKey: cfg.trackingApiKey,
      };
    }
    return result;
  } catch {
    return undefined;
  }
}

const getEffectiveConfig = (className: string): RuntimeAppConfig | undefined => {
  if (runtimeConfig?.[className]) return runtimeConfig[className];
  if (injectedConfig?.[className]) {
    console.warn(`TalknWidgets: ${className} is using build-time config (deprecated). Migrate to runtime config.`);
    return { ...injectedConfig[className], conversionChRegex: '' };
  }
  return undefined;
};

const shouldExcludeForWidget = (className: string): boolean => {
  const cfg = getEffectiveConfig(className);
  if (!cfg?.excludeUrlPattern) return false;
  try {
    return new RegExp(cfg.excludeUrlPattern).test(window.location.href);
  } catch {
    return false;
  }
};

const getInjectedTargetCh = (className: string): string | undefined => {
  const cfg = getEffectiveConfig(className);
  if (cfg?.targetCh && cfg.targetCh !== '/') return cfg.targetCh;
  return undefined;
};

export type HtmlDataOption = {
  dataMenuType?: string;
};

export type TalknWidgetRenderArgs = {
  rootElm: HTMLElement;
  connection: string;
  rankScope?: string;
  tuneOption: TuneProjection;
  className: string;
  index: number;
  remoteConfig?: RuntimeAppConfig;
  connectionMode: 'tune-shared' | 'observe';
};

export type TalknWidgetRenderer = (args: TalknWidgetRenderArgs) => Promise<void> | void;

type RenderOptions = {
  tuneOption?: TuneProjection;
};

const markWidgetRendered = (rootElm: HTMLElement): void => {
  rootElm.dataset.talknWidgetReady = '1';
  rootElm.dispatchEvent(new CustomEvent('talkn:widget-ready', { bubbles: true }));
};

const renderers = new Map<string, TalknWidgetRenderer>();

// observe モードで動作する widget クラス名
// これらは LIVE 数に影響しない購読接続を使う
const observeWidgetClasses = new Set([
  'talkn-circle',
  'talkn-badge',
  'talkn-notification',
  'talkn-heatmap',
  'talkn-card',
  'talkn-story',
  'talkn-ticker',
  'talkn-ranking',
  'talkn-charts',
  'talkn-thread', // スレッド表示はLIVE数に影響しない（talkn-postのみカウント）
]);

export const registerApp = (className: string, renderer: TalknWidgetRenderer) => {
  renderers.set(className, renderer);
  if (typeof window !== 'undefined' && window.TalknWidgets) {
    const liveApps = window.TalknWidgets.apps as Map<string, TalknWidgetRenderer>;
    if (liveApps !== renderers) {
      liveApps.set(className, renderer);
    }
  }
};

export const getConnectionWithConfig = (root: HTMLElement, className: string): string => {
  if (root?.dataset?.ch) return root.dataset.ch;

  if (window.location.search) {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.get('talkn-ch')) return urlSearchParams.get('talkn-ch')!;
    if (urlSearchParams.get('ch')) return urlSearchParams.get('ch')!;
  }

  const injectedCh = getInjectedTargetCh(className);
  if (injectedCh) return injectedCh;

  const { hostname, pathname } = window.location;
  return `/${hostname}${pathname}`;
};

export const getRankScope = (root: HTMLElement): string | undefined => {
  const scope = root?.dataset?.chScope;
  const { hostname, pathname } = window.location;

  if (scope === 'root') {
    return `/${hostname}/`;
  }
  if (scope === 'parent') {
    const full = `/${hostname}${pathname}`;
    const trimmed = full.endsWith('/') ? full.slice(0, -1) : full;
    const lastSlash = trimmed.lastIndexOf('/');
    return lastSlash > 0 ? trimmed.slice(0, lastSlash) + '/' : `/${hostname}/`;
  }
  return undefined;
};

export const getHtmlDataOption = (root: HTMLElement): HtmlDataOption => {
  const dataMenuType = root?.dataset?.menuType;
  return { dataMenuType };
};

// DOM から要素が消えたときに untune する MutationObserver を設定する。
// クロスバンドルで同一要素への重複アタッチを防ぐため DOM プロパティで管理する。
// WeakSet/WeakMap はバンドルスコープ分離のため複数バンドルをまたいで機能しない。
const attachUntuneObserver = (rootElm: HTMLElement, untune: () => Promise<void>): void => {
  // 接続変更に備えて常に最新の untune を DOM プロパティで保持（クロスバンドル共有）
  (rootElm as HTMLElement & { __talknUntuneFn?: () => Promise<void> }).__talknUntuneFn = untune;

  if ((rootElm as HTMLElement & { __talknHasObserver?: boolean }).__talknHasObserver) return;
  (rootElm as HTMLElement & { __talknHasObserver?: boolean }).__talknHasObserver = true;

  const observer = new MutationObserver(() => {
    if (!document.contains(rootElm)) {
      const elm = rootElm as HTMLElement & { __talknUntuneFn?: () => Promise<void>; __talknHasObserver?: boolean };
      const currentUntune = elm.__talknUntuneFn;
      delete elm.__talknHasObserver;
      delete elm.__talknUntuneFn;
      observer.disconnect();
      currentUntune?.().catch(() => {});
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
};

const renderByClassName = async (
  className: string,
  renderer: TalknWidgetRenderer,
  options: RenderOptions = {}
) => {
  if (shouldExcludeForWidget(className)) return;

  const roots = document.querySelectorAll(`.${className}`);
  if (!roots || roots.length === 0) return;

  const clientConfig = talknClients[className as keyof typeof talknClients];
  const clientTuneOption = clientConfig && 'tuneOption' in clientConfig ? clientConfig.tuneOption : undefined;
  const tuneOption = options.tuneOption ?? clientTuneOption ?? tuneProjectionAllTrue;

  const isObserveWidget = observeWidgetClasses.has(className);
  const connectionMode = isObserveWidget ? 'observe' : 'tune-shared';

  await Promise.all(
    Array.from(roots).map(async (root, index) => {
      const rootElm = root as HTMLElement;
      const connection = getConnectionWithConfig(rootElm, className);
      const rankScope = getRankScope(rootElm);
      const remoteConfig = getEffectiveConfig(className);

      // SPA遷移でconnectionが変わった場合、ウィジェットに再tune を指示する
      const prevCh = rootElm.dataset.talknRenderedCh;
      // 初回のみ ID を付与する（再呼び出し時の上書きは identifierPrefix に影響しないが不要）
      if (!prevCh) {
        const appId = `${className}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        rootElm.id = appId;
        rootElm.dataset.talknAppId = appId;
      }
      const isConnectionChange = Boolean(prevCh && prevCh !== connection);
      if (isConnectionChange) {
        rootElm.dataset.talknForceRetune = '1';
      }
      rootElm.dataset.talknRenderedCh = connection;

      try {
        if (isConnectionChange && prevCh) {
          await releaseSharedConnection(prevCh, connectionMode as 'tune-shared' | 'observe');
        }

        await renderer({ rootElm, connection, rankScope, tuneOption, className, index, remoteConfig, connectionMode });
        markWidgetRendered(rootElm);
      } finally {
        // renderer が例外を投げても refCount リークを防ぐために finally でアタッチ
        // releaseSharedConnection は entry が存在しない場合 no-op なので二重呼び出しも安全
        attachUntuneObserver(rootElm, () => {
          return releaseSharedConnection(connection, connectionMode as 'tune-shared' | 'observe');
        });
      }
    })
  );
};

const TalknWidgets = {
  init: async (config: TalknWidgetsConfig = {}): Promise<void> => {
    if (TalknWidgetsConfigManager.isInitialized()) {
      console.warn('TalknWidgets already initialized');
      return;
    }

    if (typeof window === 'undefined') {
      console.info('TalknWidgets: SSR environment detected, skipping initialization');
      return;
    }

    TalknWidgetsConfigManager.setConfig(config);
    applyTalknApiDefaults();

    if (!TalknWidgetsConfigManager.verifyOrigin()) {
      throw new Error('TalknWidgets: Origin not allowed');
    }

    TalknWidgetsConfigManager.markInitialized();
  },

  render: async (className: string, options: RenderOptions = {}) => {
    if (runtimeConfig === undefined) {
      const result = await fetchRuntimeConfig();
      runtimeConfig = result ?? null;
    }
    applyTalknApiDefaults();

    const renderer = renderers.get(className);
    if (!renderer) {
      throw new Error(`Renderer not registered for className: ${className}`);
    }
    await renderByClassName(className, renderer, options);
  },

  renderAll: async (options: RenderOptions = {}) => {
    if (runtimeConfig === undefined) {
      const result = await fetchRuntimeConfig();
      runtimeConfig = result ?? null;
    }
    applyTalknApiDefaults();

    if (!TalknWidgetsConfigManager.isInitialized()) {
      await TalknWidgets.init();
    }

    await Promise.all(
      Array.from(renderers.entries()).map(([className, renderer]) =>
        renderByClassName(className, renderer, options)
      )
    );

    (window as Window & { __talknRenderAllHasRun?: boolean }).__talknRenderAllHasRun = true;
  },

  apps: renderers,
};

export { TalknWidgets };
export default TalknWidgets;

declare global {
  interface Window {
    TalknWidgets: typeof TalknWidgets;
    TalknReleaseSharedConnection?: (connection: string, connectionMode: 'tune-shared' | 'observe') => Promise<void>;
  }
}

if (typeof window !== 'undefined') {
  if (!window.TalknWidgets) {
    window.TalknWidgets = TalknWidgets;
    // Backward compat: older news site code checks window.TalknApps
    (window as Window & { TalknApps?: typeof TalknWidgets }).TalknApps = TalknWidgets;
  } else {
    const renderAllHasRun = (window as Window & { __talknRenderAllHasRun?: boolean }).__talknRenderAllHasRun;
    for (const [className, renderer] of Array.from(renderers.entries())) {
      (window.TalknWidgets.apps as Map<string, TalknWidgetRenderer>).set(className, renderer);
      if (renderAllHasRun) {
        window.TalknWidgets.render(className).catch(() => {});
      }
    }
  }
}
